import { useState } from "react";
import useDefaultInputStyles from "../../../../shared/hooks/useDefaultInputStyles";
import { useFormContext } from "react-hook-form";
import AddSideModal from "../../modals/AddSideModal";
import useEditSide from "./hooks/useEditSide";
import useDeleteSide from "../../modals/ManageEventModal/hooks/useDeleteSide";
import { displayNotify } from "../../../../shared/services/Notifications/NotificationServices";

declare type SelectClasses = {
  select: string;
  select_wrapper: string;
  select_value: string;
  select_values: string;
  select_arrow: string;
  select_options: string;
  select_option: string;
  select_placeholder: string;
  select_calendar: string;
};

interface MultiSidesSelectorProps {
  className?: string,
  classes?: Partial<SelectClasses>;
  title: string,
  formName: string,
  eventSlug?: string,
  disabled: boolean,
  disabledMessage?: { title: string; description: string }
}

interface SideFormData {
  id?: string,
  name: string,
  description: string,
  is_registration_allowed: boolean,
  size_limit: number,
}


const MultiSidesSelector = ({
  className,
  title,
  classes,
  formName,
  eventSlug,
  disabled = false,
  disabledMessage,
  ...props
}: MultiSidesSelectorProps) => {
  const { mutate: editSide } = useEditSide(eventSlug)
  const { mutate: deleteSide } = useDeleteSide(eventSlug)

  const { watch, setValue, getValues } = useFormContext()

  const sides = watch(formName) as SideFormData[]
  const [isOpen, setIsOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const handleAdd = (newSide: SideFormData) => {
    const updated = [...(sides || []), newSide]
    setValue(formName, updated)
  }

  const handleLocalEdit = (index: number, updatedSide: SideFormData) => {
    const updated = [...(sides || [])]
    updated[index] = updatedSide
    setValue(formName, updated)
  }

  const handleEdit = (index: number, updatedSide: SideFormData) => {
    editSide(updatedSide)
    const updated = [...(sides || [])]
    updated[index] = updatedSide
    setValue(formName, updated)
  }

  const handleRemove = (index: number) => {
    const updated = sides.filter((_, i) => i !== index)
    setValue(formName, updated)
  }

  const handleDelete = (index: number, id: string) => {
    deleteSide(id)
    const updated = sides.filter((_, i) => i !== index)
    setValue(formName, updated)
  }

  return (
    <>
      <div
        className={`${className} ${classes?.select_wrapper}`}
        style={disabled ? { opacity: 0.3 } : undefined}
        onClickCapture={(e) => {
          if (disabled && disabledMessage) {
            e.preventDefault()
            e.stopPropagation()

            displayNotify({
              status: 'info',
              title: disabledMessage.title,
              description: disabledMessage.description,
            })
          }
        }}
      >
        {sides?.length > 0 ? (
          <div>
            <div className="select_placeholder_upper-placeholder">
              {title}
            </div>
            <div className="select_values_items">
              {sides.map((item, index) => (
                <div
                  className="select_values_item select_values_item_edit"
                  key={index}
                  onClick={() => {
                    setEditingIndex(index)
                    setIsOpen(true)
                  }
                  }
                >
                  <p className="select_values_item_title">
                    {item.name}
                  </p>
                  <div
                    className="select_values_item_edit"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M4.99995 9.5C4.99995 9.22385 5.2238 9 5.49995 9H9.49991C9.77606 9 9.99991 9.22385 9.99991 9.5C9.99991 9.77615 9.77606 10 9.49991 10H5.49995C5.2238 10 4.99995 9.77615 4.99995 9.5Z" fill="#62666D" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M9.38671 3.04997C10.1916 2.2419 10.1574 1.18076 9.55641 0.530914C9.26332 0.214049 8.84622 0.0116485 8.37377 0.00048848C7.89958 -0.0107115 7.40828 0.170634 6.95494 0.549924C6.94334 0.559624 6.93219 0.569844 6.92154 0.580554L0.437226 7.0908C0.157214 7.3719 0 7.75255 0 8.14935V8.9961C0 9.54685 0.445981 10 1.00191 10H1.84131C2.24016 10 2.6226 9.84115 2.90407 9.55855L9.38671 3.04997ZM7.60348 1.89647C7.40823 1.70121 7.09164 1.70121 6.89639 1.89647C6.70114 2.09174 6.70114 2.40832 6.89639 2.60358L7.39638 3.10358C7.59163 3.29884 7.90823 3.29884 8.10348 3.10358C8.29872 2.90832 8.29872 2.59174 8.10348 2.39647L7.60348 1.89647Z" fill="#62666D" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="select_placeholder">
            {title}
          </div>
        )}

        <div
          className="select_plus"
          onClick={() => {
            if (disabled) return
            setIsOpen(!isOpen)
            setEditingIndex(null)
          }}
        >
        </div>

      </div>
      <AddSideModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isCreate={editingIndex === null}
        initialData={editingIndex !== null ? sides[editingIndex] : undefined}
        onSubmit={(side: any) => {
          if (editingIndex === null) handleAdd(side)
          else if (side.id) handleEdit(editingIndex, side)
          else handleLocalEdit(editingIndex, side)
        }}
        onDelete={(side: any) => {
          if (editingIndex !== null && !side.id) handleRemove(editingIndex)
          if (editingIndex !== null && side.id) handleDelete(editingIndex, side.id)
        }}
      />
    </>
  )
}

export default MultiSidesSelector